import { Button } from "@material-ui/core";
import {
  Cancel,
  FiberManualRecord,
  Pause,
  PlayCircleFilled,
  Stop,
} from "@material-ui/icons";
import { useState, useRef, useEffect } from "react";
import RecordRTC from "recordrtc";
import { MAX_RECORDING_TIME, RECORDING_STATUS } from "../../utils/enums";
import DialogAlert from "./DialogAlert";
import { v4 as uuidv4 } from "uuid";
import { formatTime } from "../../utils/utils";

const VideoRecorder = ({
  setRecordedResponsFile,
  isPreview,
  setRecordingStatus,
  recordingStatus,
}) => {
  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerIntervalRef = useRef(null);

  const [videoBlobURL, setVideoBlobURL] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const [timer, setTimer] = useState(MAX_RECORDING_TIME);
  const [showAlert, setShowAlert] = useState(false);
  const [countdown, setCountdown] = useState(null);

  const startCountdown = async () => {
    const section = document.getElementById("videoPlayer");
    if (section && section.scrollIntoView) {
      section.scrollIntoView({ behavior: "smooth" });
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280, max: 1280 },
          height: { ideal: 720, max: 720 },
          frameRate: { ideal: 24, max: 30 },
        },
        audio: true,
      });
      streamRef.current = stream;

      videoRef.current.srcObject = stream;
      videoRef.current.src = "";
      await videoRef.current.play();

      let counter = 5;
      setCountdown(counter);
      setRecordingStatus(RECORDING_STATUS.started);
      setTimer(MAX_RECORDING_TIME);
      const countdownInterval = setInterval(() => {
        counter -= 1;
        if (counter === 0) {
          clearInterval(countdownInterval);
          setCountdown(null);
          startRecording(stream);
        } else {
          setCountdown(counter);
        }
      }, 1000);
    } catch (err) {
      console.error("Error accessing media devices.", err);
    }
  };

  const startRecording = (stream) => {
    try {
      const recorder = new RecordRTC(stream, {
        type: "video",
        videoBitsPerSecond: 1200000,
        audioBitsPerSecond: 96000,
        mimeType: "video/webm",
      });
      recorder.startRecording();
      recorderRef.current = recorder;

      setRecordingStatus(RECORDING_STATUS.recording);
      setIsPaused(false);
      setVideoBlobURL("");
      startTimer();
    } catch (err) {
      console.error("Error accessing media devices.", err);
    }
  };

  const stopRecording = () => {
    clearInterval(timerIntervalRef.current);

    recorderRef.current.stopRecording(() => {
      const blob = recorderRef.current.getBlob();
      const url = URL.createObjectURL(blob);
      const uniqueName = `${uuidv4()}.webm`;

      setRecordedResponsFile(
        new File([blob], uniqueName, { type: "video/webm" })
      );

      setVideoBlobURL(url);
      setRecordingStatus(RECORDING_STATUS.stopped);

      // Stop webcam stream
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;

      // Set video src to recorded blob URL
      videoRef.current.srcObject = null;
      videoRef.current.src = url;
      videoRef.current.controls = true;
      videoRef.current.play();
    });
  };

  const cancelRecording = () => {
    setVideoBlobURL("");
    videoRef.current.src = null;
    videoRef.current.srcObject = null;
    recorderRef.current = null;
    streamRef.current = null;
    setRecordingStatus(RECORDING_STATUS.not_started);
    setShowAlert(false);
    clearInterval(null);
    setTimer(MAX_RECORDING_TIME);
    setRecordedResponsFile(null);
  };

  const pauseRecording = () => {
    recorderRef.current.pauseRecording();
    setIsPaused(true);
    setRecordingStatus(RECORDING_STATUS.paused);
    clearInterval(timerIntervalRef.current);
  };

  const resumeRecording = () => {
    recorderRef.current.resumeRecording();
    setIsPaused(false);
    setRecordingStatus(RECORDING_STATUS.recording);
    startTimer();
  };

  const startTimer = () => {
    clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          stopRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      clearInterval(timerIntervalRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return (
    <div className="flex-column-center-padding">
      <div className="video-player-container" id="videoPlayer">
        <div className="d-flex flex-row justify-content-between w-100">
          <p className="mt-3 font-weight-bold">
            Recording Progress: <span>{recordingStatus}</span>
          </p>
          <div className="recording-controls">
            {(recordingStatus === RECORDING_STATUS.not_started ||
              recordingStatus === RECORDING_STATUS.stopped) && (
              <>
                <Button
                  variant="contained"
                  onClick={startCountdown}
                  className="recording-button"
                >
                  <FiberManualRecord color="error" />
                  {recordingStatus === RECORDING_STATUS.stopped
                    ? "Record Again"
                    : "Start Recording"}
                </Button>
                {!isPreview &&
                  recordingStatus !== RECORDING_STATUS.not_started && (
                    <Button
                      variant="outlined"
                      onClick={() => setShowAlert(true)}
                      disabled={recordingStatus !== RECORDING_STATUS.stopped}
                      className="cancel-button ml-3"
                    >
                      <Cancel />
                      Discard
                    </Button>
                  )}
              </>
            )}

            {recordingStatus === RECORDING_STATUS.recording && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={pauseRecording}
                  className="button-padding"
                >
                  <Pause />
                  Pause
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={stopRecording}
                  className="button-padding"
                >
                  <Stop />
                  Stop
                </Button>
              </>
            )}

            {recordingStatus === RECORDING_STATUS.paused && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={resumeRecording}
                  className="button-padding"
                >
                  <PlayCircleFilled />
                  Resume
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={stopRecording}
                  className="button-padding"
                >
                  <Stop color="pimary" />
                  Stop
                </Button>
              </>
            )}
          </div>
        </div>
        <label className="text-red font-18">Note: Recording without video, with low lighting, or with low audio volume may affect your score.</label>
        <video
          ref={videoRef}
          className="video-player"
          muted={recordingStatus !== RECORDING_STATUS.stopped}
          controls={recordingStatus === RECORDING_STATUS.stopped}
          autoPlay
          style={{
            // To be displayed in the new window
            width: "100%",
            height: "auto",
            minHeight: "360px",
          }}
        >
          Your browser does not support the video tag.
        </video>

        {recordingStatus === RECORDING_STATUS.recording && (
          <div className="outer-circle">
            <div className="inner-circle" />
          </div>
        )}
        {countdown !== null && (
          <div className="countdown-overlay">{countdown}</div>
        )}
      </div>

      <p className="font-weight-bold">
        The maximum recording time for each session is 5 minutes. | Time left:{" "}
        <span className="timer">{formatTime(timer)}</span>
      </p>

      {showAlert && (
        <DialogAlert
          show={showAlert}
          title="Remove Recorded Response"
          handleYes={cancelRecording}
          handleNo={() => setShowAlert(false)}
          positiveButtonTitle="Yes"
          negativeButtonTitle="No"
          icon={true}
        >
          Are you sure you wish to remove the recorded response? Once removed,
          you will be required to record a new response.
        </DialogAlert>
      )}
    </div>
  );
};

export default VideoRecorder;
